/** @format */
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import TransactionIcon from '@toucan/client/global/icons/legacy/transaction.svg';
import ShieldIcon from '@toucan/client/global/icons/shield-with-star.svg';
import ShippingIcon from '@toucan/client/global/icons/shipping.svg';
import XCoverIcon from '@toucan/client/global/icons/x-cover.svg';

import ReturnGoodsIcon from '@toucan/client/global/images/return-goods.svg';

import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import {
    getWarrantyLengthForMarket,
    isConditionSparesAndRepairs,
} from '@toucan/client/global/product';

import {
    SiteModalBlockFields,
    getPageDataPart,
} from '@toucan/client/core/contentful';
import {
    deShippingHelpArticle,
    deShippingHelpArticleConsolidatedHC,
} from '@toucan/client/core/shipping';

import { Link, LinkTarget } from '@toucan/client/shared/components/link';
import { TrustPilotXCover } from '@toucan/client/shared/components/trustpilot-x-cover';
import { XCoverLink } from '@toucan/client/shared/components/x-cover-link';
import { useFeatureFlaggedConfig } from '@toucan/client/shared/hooks/use-feature-flagged-config';

import { MpbApproved } from '../mpb-approved';
import { ProductTrustCard } from '../product-trust-card';
import { ProductTrustCardVariant } from '../product-trust-card/types';
import { styles } from './product-detail-sidebar.styles';
import { ProductDetailSidebarProps } from './types';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export const DEFAULT_TEST_ID = 'product-detail-sidebar';

export const StyledProductDetailSidebar = styled.div`
    ${styles}
`;

export const ProductDetailSidebar: FunctionComponent<
    ProductDetailSidebarProps
> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    product,
    pageData,
    showLowerTheCost = false,
    ...otherProps
}: ProductDetailSidebarProps) => {
    const { activeMarket } = useContext(MarketContext);
    const { t } = useTranslation();

    const shippingHelpArticle = useFeatureFlaggedConfig<string>(
        'APE-3584-(consolidate-intercom-workspaces)',
        deShippingHelpArticle,
        deShippingHelpArticleConsolidatedHC
    );

    const isGermanMarket = activeMarket.code === MarketCode.DE_DE;

    const isSparesAndRepairs = isConditionSparesAndRepairs(product?.condition);

    const learnCheckingProcess = getPageDataPart<SiteModalBlockFields>({
        data: pageData,
        siteSection: 'LEARN_CHECKING_PROCESS',
        typename: 'SiteContentModal',
    });

    const numericWarrantyMarkets = [
        MarketCode.EN_US,
        MarketCode.FR_FR,
        MarketCode.NL_NL,
        MarketCode.FR_BE,
        MarketCode.NL_BE,
        MarketCode.ES_ES,
    ];

    const shouldHaveNumericWarrantyLength = numericWarrantyMarkets.includes(
        activeMarket.code
    );
    const warrantyLength = getWarrantyLengthForMarket(
        t,
        activeMarket,
        shouldHaveNumericWarrantyLength
    );

    const warrantyString = t(
        "All used gear is covered by MPB's free {{warrantyLength}}-month warranty.",
        {
            warrantyLength,
        }
    );

    const warrantyHeading = t(`Free {{warrantyLength}}-month warranty`, {
        warrantyLength,
    });

    return (
        <StyledProductDetailSidebar {...otherProps} data-testid={dataTestId}>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {showLowerTheCost && (
                <ProductTrustCard
                    className="lower-the-cost"
                    data-testid={`${dataTestId}__lower-the-cost`}
                    icon={<TransactionIcon />}
                    heading={t('Lower the cost')}
                >
                    <p data-testid={`${dataTestId}__lower-the-cost__message`}>
                        {t(
                            "Trade in any digital camera, lens or accessory. It's quick, easy and nothing to pay today. Once you've booked your free shipping to us we'll reserve the kit you’re buying, phew."
                        )}
                    </p>
                    <WrappedLink
                        className="product-trust-card-link"
                        href="/sell-form"
                        as={`/${activeMarket.slug}/${t('sell-or-trade', {
                            ns: 'urls',
                        })}`}
                        passHref
                        data-testid={`${dataTestId}__lower-the-cost__call-to-action`}
                        target={LinkTarget.SELF}
                    >
                        {t('Get an instant quote')}
                    </WrappedLink>
                </ProductTrustCard>
            )}

            {!isSparesAndRepairs && (
                <ProductTrustCard
                    icon={<ShieldIcon />}
                    heading={warrantyHeading}
                    data-testid={`${dataTestId}__product-warranty`}
                >
                    <p data-testid={`${dataTestId}__product-warranty__message`}>
                        {warrantyString}
                    </p>
                </ProductTrustCard>
            )}

            <ProductTrustCard
                icon={<XCoverIcon />}
                heading={t('Add Extended Product Protection')}
                data-testid={`${dataTestId}__product-protection`}
                variant={ProductTrustCardVariant.SUN}
                withKeyline={!isSparesAndRepairs}
                className="keyline-container"
            >
                <div className="product-protection-body">
                    <p
                        data-testid={`${dataTestId}__product-protection__message`}
                    >
                        {t(
                            'At checkout, add coverage for accidental damage and extend your free MPB twelve-month warranty for a total of two or three years.'
                        )}
                    </p>
                    <XCoverLink />
                    <TrustPilotXCover />
                </div>
            </ProductTrustCard>

            {!isSparesAndRepairs && (
                <ProductTrustCard
                    icon={<ReturnGoodsIcon />}
                    heading={t('No hassle returns')}
                    data-testid={`${dataTestId}__product-returns`}
                >
                    <p data-testid={`${dataTestId}__product-returns__message`}>
                        {t(
                            "If you find the item isn't as described, you can return it free for a full refund within 14 days of arrival."
                        )}
                    </p>
                </ProductTrustCard>
            )}

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {learnCheckingProcess && (
                <MpbApproved
                    learnCheckingProcess={learnCheckingProcess}
                    className="mpb-approved"
                    data-testid={`${dataTestId}__mpb-approved`}
                />
            )}

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {isGermanMarket && (
                <ProductTrustCard
                    icon={<ShippingIcon />}
                    heading={t('Delivery')}
                    data-testid={`${dataTestId}__delivery-block`}
                >
                    <p data-testid={`${dataTestId}__delivery-block__message`}>
                        {t(
                            'Free shipping if you trade otherwise shipping costs apply.'
                        )}
                    </p>
                    <Link
                        href={shippingHelpArticle}
                        target={LinkTarget.BLANK}
                        data-testid={`${dataTestId}__call-to-action`}
                    >
                        {t('Find out more')}
                    </Link>
                </ProductTrustCard>
            )}
        </StyledProductDetailSidebar>
    );
};
